import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { IngestionJob, IngestionJobType, IngestionStatus } from '../../../../core/models/ingestion.models';

@Component({
  selector: 'app-ingestion-list',
  standalone: true,
  imports: [CommonModule, OverlayModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ingestion-list.component.html',
  styleUrls: ['./ingestion-list.component.scss']
})
export class IngestionListComponent {
  @Input() jobs: IngestionJob[] = [];
  @Input() isLoading = false;
  @Input() listError: string | null = null;
  @Input() currentPage = 1;
  @Input() totalPages = 1;
  @Input() totalCount = 0;
  @Output() pageChange = new EventEmitter<number>();

  private cdr = inject(ChangeDetectorRef);
  activeTooltipJobId: string | null = null;

  get pageNumbers(): number[] {
    const delta = 2;
    const start = Math.max(1, this.currentPage - delta);
    const end = Math.min(this.totalPages, this.currentPage + delta);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  readonly tooltipPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
  ];

  showTooltip(jobId: string): void { this.activeTooltipJobId = jobId; this.cdr.markForCheck(); }
  hideTooltip(): void { this.activeTooltipJobId = null; this.cdr.markForCheck(); }

  statusClass(status: IngestionStatus): string {
    switch (status) {
      case 'DONE':
        return 'bg-green-500/10 text-green-400 border border-green-500/20';
      case 'FAILED':
        return 'bg-red-500/10 text-red-400 border border-red-500/20';
      case 'RUNNING':
        return 'bg-blue-500/10 text-blue-400 border border-blue-500/20';
      case 'DELETED':
        return 'bg-amber-500/10 text-amber-400 border border-amber-500/20';
      case 'PENDING':
      default:
        return 'bg-gray-500/10 text-gray-300 border border-gray-500/20';
    }
  }

  initials(name: string | undefined): string {
    if (!name) return 'VU';
    return name.substring(0, 2).toUpperCase();
  }

  formatBytes(bytes: number | null): string {
    if (bytes === null || bytes === undefined) return '—';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }

  detailsIconClass(status: IngestionStatus): string {
    switch (status) {
      case 'FAILED':
        return 'text-red-400 hover:bg-red-500/10';
      case 'RUNNING':
        return 'text-blue-400 hover:bg-blue-500/10';
      case 'DONE':
        return 'text-green-400 hover:bg-green-500/10';
      default:
        return 'text-gray-400 hover:bg-gray-500/10';
    }
  }

  jobTypeClass(_type: IngestionJobType): string {
    return 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700';
  }

  trackByJob = (_: number, job: IngestionJob): string => job.id;
}
